<template>
  <div class="modal-backdrop" @click.self="$emit('close')">
    <div class="modal-content">
      <header class="modal-header">
        <h2>Feedback & Suggestions</h2>
        <button class="close-btn" @click="$emit('close')">×</button>
      </header>

      <div class="modal-body">
        <div class="feedback-form">
            <div class="rating-section">
                <h3>Rate your experience</h3>
                <div class="stars">
                    <button 
                        v-for="star in 5" 
                        :key="star" 
                        :class="['star-btn', { active: rating >= star }]"
                        @click="rating = star"
                        @mouseover="hoverRating = star"
                        @mouseleave="hoverRating = 0"
                        :aria-label="'Rate ' + star + ' stars'"
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor" class="star-icon">
                          <path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
                <p class="rating-label">{{ ratingLabels[rating - 1] || 'Select a rating' }}</p>
            </div>

            <div class="comment-section">
                <label for="feedback-text">Any suggestions or issues?</label>
                <textarea 
                    id="feedback-text" 
                    v-model="comment" 
                    placeholder="Tell us what you like or what's broken..."
                    rows="4"
                ></textarea>
            </div>
        </div>
      </div>

      <footer class="modal-footer">
        <button class="btn-secondary" @click="$emit('close')">Cancel</button>
        <button 
            class="btn-primary" 
            @click="submitFeedback" 
            :disabled="submitting || rating === 0"
        >
            {{ submitting ? 'Sending...' : 'Send Feedback' }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SurveyModal',
  data() {
    return {
      rating: 0,
      hoverRating: 0,
      comment: '',
      submitting: false,
      ratingLabels: ['Terrible', 'Bad', 'Okay', 'Good', 'Amazing!']
    };
  },
  methods: {
    async submitFeedback() {
      if (this.rating === 0) return;
      
      this.submitting = true;
      try {
        await this.$store.dispatch('submitSurvey', {
            rating: this.rating,
            comment: this.comment
        });
        
        this.$emit('completed');
        this.$emit('close');
      } catch (err) {
        // Error handled in store
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #18181b;
  width: 100%;
  max-width: 450px;
  border-radius: 4px; /* Sharp corners */
  border: 1px solid #27272a;
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.8);
  overflow: hidden;
  animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.modal-header {
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #27272a;
  background: #121215;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.close-btn {
  background: none;
  border: none;
  color: #71717a;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #fff;
}

.modal-body {
  padding: 24px;
}

.rating-section {
    text-align: center;
    margin-bottom: 24px;
}

.rating-section h3 {
    margin: 0 0 12px 0;
    color: #a1a1aa;
    font-size: 0.9rem;
    text-transform: uppercase;
    font-weight: 600;
}

.stars {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-bottom: 8px;
}

.star-btn {
    background: none;
    border: none;
    color: #3f3f46; /* darker gray */
    cursor: pointer;
    transition: transform 0.1s, color 0.1s;
    padding: 4px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
}

.star-icon {
    width: 32px;
    height: 32px;
}

.star-btn:hover {
    transform: translateY(-2px);
    color: #71717a;
}

.star-btn.active {
    color: #f59e0b; /* Gamer Orange/Gold */
    filter: drop-shadow(0 0 5px rgba(245, 158, 11, 0.4));
}

.rating-label {
    height: 1.2rem;
    color: #f59e0b;
    font-weight: 700;
    font-size: 0.9rem;
    margin: 0;
    text-transform: uppercase;
}

.comment-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.comment-section label {
    font-weight: 600;
    font-size: 0.85rem;
    color: #a1a1aa;
    text-transform: uppercase;
}

textarea {
    background: #09090b;
    border: 1px solid #3f3f46;
    border-radius: 4px; /* Sharp */
    padding: 12px;
    color: #fff;
    resize: vertical;
    font-family: inherit;
    font-size: 0.95rem;
    transition: border-color 0.2s;
}

textarea:focus {
    outline: none;
    border-color: #00dc82;
}

textarea::placeholder {
  color: #52525b;
}

.modal-footer {
  padding: 16px 24px;
  background: #121215;
  border-top: 1px solid #27272a;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn-secondary {
  background: transparent;
  border: 1px solid #3f3f46;
  color: #a1a1aa;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85rem;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background: #27272a;
  color: #fff;
  border-color: #52525b;
}

.btn-primary {
  background: #00dc82;
  color: #000;
  border: none;
  padding: 10px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.85rem;
  transition: all 0.2s;
}

.btn-primary:hover:not(:disabled) {
    background: #10b981;
    transform: translateY(-1px);
    box-shadow: 0 4px 0 #047857;
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: none;
}

.btn-primary:disabled {
  background: #27272a;
  color: #52525b;
  cursor: not-allowed;
}
</style>
